/**
 * TransferEdge - Visual representation of a transfer between accounts
 *
 * Renders a dashed orange horizontal line with an arrow head pointing to the target,
 * with the amount labeled in the middle. Used in the TransferSection component.
 */

import { memo, useState, useCallback } from 'react';
import { COLORS, A11Y } from '../constants';

export interface TransferEdgeProps {
  sourceAccount: string;
  targetAccount: string;
  amount: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  onClick?: () => void;
  onHover?: (hovering: boolean, event?: React.MouseEvent) => void;
  isHighlighted?: boolean;
}

/**
 * Format currency amount for display
 */
function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function TransferEdgeComponent({
  sourceAccount,
  targetAccount,
  amount,
  sourceX,
  sourceY,
  targetX,
  targetY,
  onClick,
  onHover,
  isHighlighted = false,
}: TransferEdgeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setIsHovered(true);
      onHover?.(true, e);
    },
    [onHover]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHover?.(false);
  }, [onHover]);

  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    },
    [onClick]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Determine visual state
  const showHighlight = isHovered || isFocused || isHighlighted;

  // Calculate arrow head position
  const arrowSize = 8;
  const arrowX = targetX - arrowSize;
  const arrowPoints = `${targetX},${targetY} ${arrowX},${targetY - arrowSize / 2} ${arrowX},${targetY + arrowSize / 2}`;

  // Calculate label position (middle of the line)
  const labelX = (sourceX + targetX) / 2;
  const labelY = sourceY;

  // Line stroke width
  const strokeWidth = 3;

  // Colors
  const lineColor = showHighlight
    ? COLORS.edgeHover.transfer
    : COLORS.edge.transfer;
  const solidColor = COLORS.transfer.border;

  return (
    <g className="transfer-edge-group">
      {/* Focus/highlight background */}
      {(isFocused || isHighlighted) && (
        <line
          x1={sourceX}
          y1={sourceY}
          x2={targetX}
          y2={targetY}
          stroke={A11Y.FOCUS_RING.split(' ')[2]}
          strokeWidth={strokeWidth + 4}
          strokeLinecap="round"
          strokeDasharray="8,4"
          opacity={0.3}
          className="pointer-events-none"
        />
      )}

      {/* Main dashed line */}
      <line
        x1={sourceX}
        y1={sourceY}
        x2={targetX - arrowSize}
        y2={targetY}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray="8,4"
        className="transfer-edge transition-all duration-150"
        role="button"
        tabIndex={0}
        aria-label={`Transfer from ${sourceAccount} to ${targetAccount}: ${formatAmount(amount)}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      />

      {/* Arrow head */}
      <polygon
        points={arrowPoints}
        fill={solidColor}
        className="transfer-arrow transition-all duration-150"
        style={{ opacity: showHighlight ? 1 : 0.7 }}
      />

      {/* Amount label */}
      <text
        x={labelX}
        y={labelY - 8}
        textAnchor="middle"
        fill={solidColor}
        fontSize={12}
        fontWeight={600}
        className="transfer-amount select-none pointer-events-none"
      >
        {formatAmount(amount)}
      </text>
    </g>
  );
}

export const TransferEdge = memo(TransferEdgeComponent);
export default TransferEdge;
